import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import { buildSearchIndex, searchCards } from '../searchCards';

function Highlight({ text, query, color }) {
  const match = text.toLocaleLowerCase().indexOf(query.trim().replace(/\s+/g, ' ').toLocaleLowerCase());
  if (match < 0) return text;
  return <>
    {text.slice(0, match)}
    <mark style={{ background: `${color}55`, color: 'inherit', borderRadius: 2 }}>{text.slice(match, match + query.trim().replace(/\s+/g, ' ').length)}</mark>
    {text.slice(match + query.trim().replace(/\s+/g, ' ').length)}
  </>;
}

export default function CardSearch({ boxes, planTitle, color, onSelect, isMobile, open, setOpen, triggerRef }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const resultRefs = useRef([]);
  const index = useMemo(() => buildSearchIndex(boxes, planTitle), [boxes, planTitle]);
  const results = useMemo(() => searchCards(index, query), [index, query]);

  function closeSearch() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  useEffect(() => {
    function onShortcut(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (open) inputRef.current?.focus();
        else setOpen(true);
      }
    }
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, [open, setOpen]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (open) resultRefs.current[selected]?.scrollIntoView?.({ block: 'nearest' });
  }, [open, selected, results]);

  function selectResult(result) {
    closeSearch();
    onSelect(result.box);
  }

  function onKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearch();
    } else if (event.key === 'ArrowDown' && results.length) {
      event.preventDefault();
      setSelected(current => Math.min(current + 1, results.length - 1));
    } else if (event.key === 'ArrowUp' && results.length) {
      event.preventDefault();
      setSelected(current => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && results.length && event.target === inputRef.current) {
      event.preventDefault();
      selectResult(results[Math.min(selected, results.length - 1)]);
    } else if (event.key === 'Tab') {
      const focusable = [...event.currentTarget.querySelectorAll('input, button')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  return open ? createPortal(
      <div
        onMouseDown={event => { if (event.target === event.currentTarget) closeSearch(); }}
        style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: isMobile ? '8vh 12px' : '15vh 20px' }}
      >
        <div
          role="dialog" aria-modal="true" aria-label={`Search cards in ${planTitle}`}
          onKeyDown={onKeyDown}
          style={{ width: '100%', maxWidth: 620, maxHeight: '75vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-2)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 20px 70px rgba(0,0,0,0.35)', overflow: 'hidden' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
            <Search size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              ref={inputRef}
              value={query}
              onChange={event => { setQuery(event.target.value); setSelected(0); }}
              placeholder={`Search cards in ${planTitle}…`}
              aria-label="Search term"
              aria-controls="card-search-results"
              aria-activedescendant={results.length ? `card-search-result-${Math.min(selected, results.length - 1)}` : undefined}
              style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: 'var(--text-primary)', font: 'inherit', fontSize: 15 }}
            />
            <button type="button" onClick={closeSearch} aria-label="Close search" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}><X size={18} /></button>
          </div>
          <div id="card-search-results" role="listbox" aria-label="Matching cards" style={{ overflowY: 'auto', padding: 6 }}>
            {!query.trim() && <div style={{ padding: 18, color: 'var(--text-muted)', fontSize: 13 }}>Search card titles and content in this plan.</div>}
            {query.trim() && !results.length && <div style={{ padding: 18, color: 'var(--text-muted)', fontSize: 13 }}>No matching cards.</div>}
            {results.map((result, position) => <button
              key={result.box.id}
              id={`card-search-result-${position}`}
              ref={element => { resultRefs.current[position] = element; }}
              role="option" aria-selected={position === selected}
              type="button"
              onMouseEnter={() => setSelected(position)}
              onClick={() => selectResult(result)}
              style={{ display: 'block', width: '100%', padding: '10px 12px', textAlign: 'left', font: 'inherit', border: 'none', borderRadius: 8, cursor: 'pointer', background: position === selected ? `${color}22` : 'transparent', color: 'var(--text-primary)' }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 10px' }}>
                <span style={{ fontWeight: 650, fontSize: 14 }}><Highlight text={result.title} query={query} color={color} /></span>
                <span style={{ color: 'var(--text-muted)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{result.path}</span>
              </div>
              {result.snippet && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Highlight text={result.snippet} query={query} color={color} /></div>}
            </button>)}
          </div>
          {!!results.length && <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)' }}>↑ ↓ select · Enter open · Esc close{results.length === 50 ? ' · First 50 results' : ''}</div>}
        </div>
      </div>, document.body
    ) : null;
}
